import { useState, useEffect, Fragment } from "react";
import axios from "axios";
import { CaretRight, CaretDown, WarningCircle, CheckCircle } from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";
import { ConfirmationDetailTable, WipBadge } from "./ConfirmationDetailTable";
import { formatQty, formatUnitTotals } from "../lib/confirmedProduction";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatAt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN") : "—");
const COLUMNS = ["", "Lot ID", "Output Product", "Description", "Model ID", "Site", "Final Operation", "Finished Qty", "UOM", "Open Qty", "Scrap", "By-product", "Confirmations", "Latest Confirmation", "WIP Clearing", "Confirmed By"];

// User's choice, Oct 22 2026: this app's records never shrink, so a
// confirmation cancelled directly in SAP would still show here. Opening a lot
// reads SAP's own total for the final reporting point - net of cancellations -
// and says plainly when the two disagree.
const SapCrossCheck = ({ lot }) => {
  const [state, setState] = useState({ loading: true });
  const lotId = lot.production_lot_id;
  useEffect(() => {
    let live = true;
    setState({ loading: true });
    axios.get(`${API}/production-confirmation/lot-sap-check/${lotId}`)
      .then(({ data }) => live && setState({ loading: false, data }))
      .catch((e) => live && setState({ loading: false, error: e.response?.data?.detail || "SAP could not be reached" }));
    return () => { live = false; };
  }, [lotId]);
  if (state.loading) return <p className="text-xs text-[#667085]" data-testid="sap-check-loading">Reading lot {lot.production_lot_id} from SAP…</p>;
  if (state.error) return <p className="text-xs text-[#B42318]" data-testid="sap-check-error">{state.error}</p>;
  if (!state.data?.found) return <p className="text-xs text-[#B54708]" data-testid="sap-check-missing">SAP no longer returns lot {lot.production_lot_id}.</p>;
  const sapQty = state.data.final?.total_confirmed_quantity;
  // Compared against every END confirmation this app holds for the lot, not
  // just the ones inside the active date/site filter - otherwise a lot
  // confirmed across two months would look like SAP had cancelled something.
  const appQty = state.data.app_records?.final_quantity ?? lot.quantity;
  const variance = sapQty == null ? null : Math.round((sapQty - appQty) * 1000) / 1000;
  const partialView = Math.abs(appQty - lot.quantity) > 0.0001;
  return (
    <div className="space-y-1.5" data-testid="sap-check-result">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs">
        <span className="text-[#475467]">App records (all dates): <strong className="tabular-nums">{formatQty(appQty)} {lot.uom}</strong></span>
        <span className="text-[#475467]">SAP now: <strong className="tabular-nums">{formatQty(sapQty)} {state.data.final?.unit_code || lot.uom}</strong></span>
        {state.data.lot_status && <span className="text-[#667085]">Lot status: {state.data.lot_status}</span>}
        {variance === 0 ? (
          <Badge variant="outline" className="bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]" data-testid="sap-check-match">
            <CheckCircle size={12} className="mr-1" /> Matches SAP
          </Badge>
        ) : (
          <Badge variant="outline" className="bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]" data-testid="sap-check-variance">
            <WarningCircle size={12} className="mr-1" /> SAP is {formatQty(Math.abs(variance))} {lot.uom} {variance < 0 ? "lower" : "higher"} - cancelled or reversed in SAP
          </Badge>
        )}
      </div>
      <p className="text-[11px] text-[#667085]">
        SAP's figure is the source of truth for the finished quantity; the transactions below are this app's original records and are never altered.
        {partialView && <> This row shows <strong>{formatQty(lot.quantity)} {lot.uom}</strong> because the rest of this lot was confirmed outside the current filters.</>}
      </p>
    </div>
  );
};

export const FinishedProductionTable = ({ lots, reportingPointFilter }) => {
  const [openKey, setOpenKey] = useState(null);
  return (
    <div className="border border-[#D0D5DD] rounded-sm overflow-auto bg-white">
      <table className="w-full text-[13px] border-collapse" data-testid="finished-production-table">
        <thead>
          <tr>
            {COLUMNS.map((h, i) => (
              <th key={h || i} className="bg-[#EAECF0] border border-[#D0D5DD] p-1.5 text-left text-xs font-bold text-[#344054] font-heading uppercase tracking-wide whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lots.map((lot, i) => (
            <Fragment key={lot.key}>
              <tr
                className={`cursor-pointer hover:bg-[#F2F4F7] ${i % 2 === 0 ? "bg-white" : "bg-[#F9FAFB]"}`}
                onClick={() => setOpenKey(openKey === lot.key ? null : lot.key)}
                data-testid={`finished-row-${lot.production_lot_id}`}
              >
                <td className="border border-[#D0D5DD] px-1.5 py-1.5 text-[#667085]">
                  {openKey === lot.key ? <CaretDown size={13} weight="bold" /> : <CaretRight size={13} weight="bold" />}
                </td>
                <td className="border border-[#D0D5DD] px-2 py-1.5 font-medium text-[#101828]">{lot.production_lot_id}</td>
                <td className="border border-[#D0D5DD] px-2 py-1.5 font-mono">{lot.main_output_product || "—"}</td>
                <td className="border border-[#D0D5DD] px-2 py-1.5 text-[#475467]">{lot.main_output_product_description || "—"}</td>
                <td className="border border-[#D0D5DD] px-2 py-1.5 text-[#475467]">{lot.production_model_id || "—"}</td>
                <td className="border border-[#D0D5DD] px-2 py-1.5 text-[#475467]">{lot.site_id || "—"}</td>
                <td className="border border-[#D0D5DD] px-2 py-1.5 text-[#475467] whitespace-nowrap">
                  {lot.finalOperationId === "END" || lot.finalOperationName === "—"
                    ? "END"
                    : <>{lot.finalOperationId} - {lot.finalOperationName}<span className="ml-1 text-[10px] font-bold text-[#0E7C86]">(END)</span></>}
                </td>
                <td className="border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums font-bold text-[#0E7C86]" data-testid={`finished-qty-${lot.production_lot_id}`}>{formatQty(lot.quantity)}</td>
                <td className="border border-[#D0D5DD] px-2 py-1.5 text-[#475467]">{lot.uom}</td>
                <td
                  className={`border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums ${lot.openQuantity ? "font-bold text-[#B54708]" : "text-[#667085]"}`}
                  data-testid={`finished-open-qty-${lot.production_lot_id}`}
                >
                  {lot.openQuantity == null ? "—" : formatQty(lot.openQuantity)}
                </td>
                <td className="border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums">{formatQty(lot.scrap)}</td>
                <td className="border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums">{formatUnitTotals(lot.byproduct)}</td>
                <td className="border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums">{lot.confirmations}</td>
                <td className="border border-[#D0D5DD] px-2 py-1.5 whitespace-nowrap text-[#475467]">{formatAt(lot.latestAt)}</td>
                <td className="border border-[#D0D5DD] px-2 py-1.5"><WipBadge wip={lot.wipClearing} /></td>
                <td className="border border-[#D0D5DD] px-2 py-1.5 text-[#475467]">{lot.actor || "—"}</td>
              </tr>
              {openKey === lot.key && (
                <tr data-testid={`finished-detail-${lot.production_lot_id}`}>
                  <td colSpan={COLUMNS.length} className="border border-[#D0D5DD] bg-[#F9FAFB] p-3 space-y-2">
                    <SapCrossCheck lot={lot} />
                    <ConfirmationDetailTable transactions={lot.transactions} testIdPrefix={`finished-${lot.production_lot_id}`} />
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
          {lots.length === 0 && (
            <tr>
              <td colSpan={COLUMNS.length} className="border border-[#D0D5DD] py-8 text-center text-[13px] text-[#667085]" data-testid="finished-empty-state">
                {reportingPointFilter?.trim()
                  ? `No final reporting point matches "${reportingPointFilter.trim()}" - clear that filter to see finished production.`
                  : "No lot reached its final reporting point (END) in this range - operation confirmations alone are not finished production."}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};
